import React from 'react';
import { Card, Empty } from 'antd';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import './StatisticsLineChart.scss';

const StatisticsLineChart = ({ 
  title, 
  currentPeriodData = [], 
  previousPeriodData = [], 
  dataKey = 'value',
  dataLabel = 'Valor',
  loading = false,
  formatValue = (val) => val,
  yAxisFormatter = (val) => val
}) => {
  // Combine the data for the chart
  const combinedData = [];
  const maxLength = Math.max(currentPeriodData.length, previousPeriodData.length);
  
  for (let i = 0; i < maxLength; i++) {
    const currentItem = currentPeriodData[i];
    const previousItem = previousPeriodData[i];
    
    const dataPoint = {
      date: currentItem?.date || previousItem?.date || `Dia ${i + 1}`,
      periodoAtual: currentItem ? currentItem[dataKey] : 0,
      periodoAnterior: previousItem ? previousItem[dataKey] : 0,
    };
    
    combinedData.push(dataPoint);
  }

  const formatTooltipValue = (value, name) => {
    return [formatValue(value), name];
  };

  const formatAxisLabel = (value) => {
    if (value && value.includes('-')) {
      // Format date from YYYY-MM-DD to DD/MM
      const [, month, day] = value.split('-');
      return `${day}/${month}`;
    }
    return value;
  };

  // Calculate totals and growth percentage
  const currentTotal = currentPeriodData.reduce((sum, item) => sum + (item[dataKey] || 0), 0);
  const previousTotal = previousPeriodData.reduce((sum, item) => sum + (item[dataKey] || 0), 0);
  const growthPercentage = previousTotal > 0 
    ? ((currentTotal - previousTotal) / previousTotal * 100).toFixed(2)
    : currentTotal > 0 ? 100 : 0;

  // Check if we have any data
  const hasData = currentPeriodData.length > 0 || previousPeriodData.length > 0;

  return (
    <Card 
      className="statistics-line-chart" 
      title={title}
      loading={loading}
      extra={
        hasData && (
          <span 
            className="growth-indicator"
            style={{ 
              color: growthPercentage >= 0 ? '#52c41a' : '#ff4d4f',
              fontWeight: 'bold'
            }}
          >
            {growthPercentage >= 0 ? '↑' : '↓'} {Math.abs(growthPercentage)}%
          </span>
        )
      }
    >
      {!hasData ? (
        <div className="empty-state">
          <Empty 
            description="Não há dados disponíveis"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={combinedData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatAxisLabel}
            />
            <YAxis tickFormatter={yAxisFormatter} />
            <Tooltip 
              formatter={formatTooltipValue}
              labelFormatter={(label) => `Data: ${formatAxisLabel(label)}`}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="periodoAtual" 
              stroke="#1890ff" 
              strokeWidth={2}
              name="Período Atual"
              dot={{ r: 4 }}
            />
            <Line 
              type="monotone" 
              dataKey="periodoAnterior" 
              stroke="#52c41a" 
              strokeWidth={2}
              name="Período Anterior"
              dot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
};

export default StatisticsLineChart;
